import React, { useRef } from 'react';
import { BsCheckCircle } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';

import { CircularProgress } from '@material-ui/core';
import { Form, FormikProvider, useFormik } from 'formik';

import Button from '~/components/Button';
import Input from '~/components/Formik/Input';
import InputPassword from '~/components/Formik/inputPassword';
import { useAuth } from '~/hooks/auth';
import { IState } from '~/store';
import { changeUserPasswordRequest } from '~/store/modules/users/actions';

import PasswordValidationSchema from './PassowordValidationSchema';
import {
  Container,
  InputContent,
  PasswordAcceptBox,
  PasswordAcceptOption,
  PasswordAcceptText,
  PasswordMessage,
} from './styles';

interface SubmitData {
  currentPassword: string;
  newPassword: string;
  confirmedNewPassword: string;
}

const ChangePasswordForm = () => {
  const formTableRef = useRef(null);

  const dispatch = useDispatch();
  const { user } = useAuth();

  const loading = useSelector((state: IState) => state.users.loading);
  const changePasswordStatus = useSelector(
    (state: IState) => state.users.changePasswordStatus,
  );

  const initialValues = {
    currentPassword: '',
    newPassword: '',
    confirmedNewPassword: '',
  };

  const onSubmit = async (data: SubmitData) => {
    const { currentPassword, newPassword } = data;

    dispatch(
      changeUserPasswordRequest(
        user.id,
        undefined,
        currentPassword,
        newPassword,
        user.personId,
      ),
    );
  };

  if (changePasswordStatus === 'success') {
    user.mustChangePassword = false;
    localStorage.setItem('@Afferolab:user', JSON.stringify(user));
  }

  const passwordHasCapitalAndLowerCaseLetters = (password: string) => {
    return password.match(/(?=.*[a-z])(?=.*[A-Z])/) != null;
  };

  const passwordHasNumber = (password: string) => {
    return password.match(/[0-9]/) != null;
  };

  const passwordHasMinLength = (password: string) => {
    return password.length >= 8;
  };

  const passwordHasSpecialCharacters = (password: string) => {
    return password.match(/[!@#$%^&*]/) != null;
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema: PasswordValidationSchema,
  });

  return (
    <Container>
      <FormikProvider value={formik}>
        <Form ref={formTableRef} onSubmit={formik.handleSubmit}>
          <InputContent>
            <Input
              name="currentPassword"
              label="Senha atual"
              placeholder="Senha atual"
              value={formik.values.currentPassword}
              type="password"
            />

            <InputPassword
              name="newPassword"
              label="Nova senha"
              placeholder="Nova senha"
              value={formik.values.newPassword}
            />
            <InputPassword
              name="confirmedNewPassword"
              label="Repita a nova senha"
              placeholder="Repita a nova senha"
              value={formik.values.confirmedNewPassword}
            />
          </InputContent>

          <PasswordMessage>Sua senha deve conter no mínimo:</PasswordMessage>
          <PasswordAcceptBox>
            <PasswordAcceptOption>
              <BsCheckCircle
                size={20}
                color={
                  passwordHasMinLength(formik.values.newPassword)
                    ? '#3f9b35'
                    : '#e6e6e6'
                }
              />
              <PasswordAcceptText>08 Caracteres</PasswordAcceptText>
            </PasswordAcceptOption>
            <PasswordAcceptOption>
              <BsCheckCircle
                size={20}
                color={
                  passwordHasCapitalAndLowerCaseLetters(
                    formik.values.newPassword,
                  )
                    ? '#3f9b35'
                    : '#e6e6e6'
                }
              />
              <PasswordAcceptText>
                Letra maiúscula e minúscula
              </PasswordAcceptText>
            </PasswordAcceptOption>
            <PasswordAcceptOption>
              <BsCheckCircle
                size={20}
                color={
                  passwordHasSpecialCharacters(formik.values.newPassword)
                    ? '#3f9b35'
                    : '#e6e6e6'
                }
              />
              <PasswordAcceptText>
                Caracter especial Exemplo: ! @ # $ % ^ & *
              </PasswordAcceptText>
            </PasswordAcceptOption>
            <PasswordAcceptOption>
              <BsCheckCircle
                size={20}
                color={
                  passwordHasNumber(formik.values.newPassword)
                    ? '#3f9b35'
                    : '#e6e6e6'
                }
              />
              <PasswordAcceptText>Número</PasswordAcceptText>
            </PasswordAcceptOption>
          </PasswordAcceptBox>
          <Button
            type="submit"
            disabled={formik.errors && Object.keys(formik.errors).length > 0}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Confirmar redefinição'
            )}
          </Button>
        </Form>
      </FormikProvider>
    </Container>
  );
};

export default ChangePasswordForm;
